// ./src/contexts/PageChromeContext/PageChromeContext.tsx
//
// ROADMAP A2b #58 — how a PAGE tells the hoisted APP SHELL what to put in its header.
//
// ⚠⚠ THIS EXISTS BECAUSE THE SHELL NO LONGER LIVES INSIDE THE PAGE. Until #58, every page rendered
// `DashboardLayout` itself and simply passed `headerSearch` / `mobileHeaderActions` / … as props.
// That is also why the shell was thrown away on every cross-page navigation: two pages are two
// different React component types, so React discarded the whole subtree, shell included.
// The shell is now rendered ABOVE the route (see `AppShellLayout`), which means a page can no
// longer hand it props — the header has to be published sideways. This is that channel.
//
// ⚠⚠ THE PROVIDER'S VALUE NEVER CHANGES, AND THAT IS THE WHOLE TRICK. It sits above the routes, so
// putting the chrome in `useState` here would re-render EVERY page on every header update — and
// since a page republishes its chrome on each of its own renders, that is an infinite loop. Instead
// the value is a stable store object; only the shell subscribes (`useSyncExternalStore`), so only
// the shell re-renders. The page is `children` of the shell, so a shell re-render does not touch it
// (same element reference → React bails out of that subtree).
//
// ⚠ PUBLISHED ON EVERY RENDER, NOT ONCE ON MOUNT. The chrome carries CLOSURES — `onSearch`,
// `onClick`, `onOpen` — over the page's current state. A one-shot hand-off would leave the header
// calling the handlers as they were when the page opened: the button works, nothing errors, and it
// acts on stale values. (The same trap the shortcut rule in em-scaffold-ui describes for `onReady`.)
//
// ⚠ PUBLISHED FROM A **LAYOUT** EFFECT, NOT `useEffect`. Layout effects run after the DOM is
// mutated but BEFORE the browser paints, and React flushes the resulting shell re-render in the
// same frame. With a passive effect the user would see one painted frame of the PREVIOUS page's
// header sitting over the new page's content on every navigation.
import React, { createContext, useContext, useMemo, useRef, useSyncExternalStore } from "react";

import type {
  HeaderFilterConfig,
  HeaderSearchConfig,
  MobileHeaderAction,
} from "@/components/layouts/DashboardLayout/DashboardLayout";
import type { FooterMessageType } from "@/components/layouts/DashboardLayout/constants";

// ============================================
// Types
// ============================================

/**
 * Everything a page may put into the app shell's header, footer and action bar.
 *
 * ⚠ This is deliberately the SAME shape `DashboardLayout` already accepted as props — the shell
 * spreads it straight back in. A second vocabulary here would drift from the layout's.
 */
export interface PageChromeValue {
  /** Override the header title (normally the sitemap page name). */
  headerTitle?: string;
  /** Override the header subtitle. Pass "" to hide the sitemap subtitle entirely. */
  headerSubtitle?: string;
  /** Debounced search box in the header (replaces a per-page search toolbar). */
  headerSearch?: HeaderSearchConfig;
  /** "Filters" button in the MOBILE header — badge + opens the page's FilterDrawer. */
  headerFilter?: HeaderFilterConfig;
  /** Action buttons in the header (right side, desktop + mobile). */
  mobileHeaderActions?: MobileHeaderAction[];
  /** The page-level action bar pinned to the bottom of the content area (the save bar). */
  stickyFooter?: React.ReactNode;
  /** Footer message shown under the content. */
  footerMessage?: string;
  footerMessageType?: FooterMessageType;
  showFooterMessage?: boolean;
  /**
   * The page needs the whole phone screen for now (a camera scanning at a door): the event strip, the section tabs and the
   * bottom bar step aside. The header stays, with Back in it. No effect on wider screens.
   */
  focusMode?: boolean;
}

/**
 * What the shell reads: the published chrome plus WHICH PAGE PUBLISHED IT.
 *
 * ⚠⚠ `ownerId` is not bookkeeping — the shell needs it to know when to throw away header state it
 * owns itself. The header search box is the shell's, not the page's: without an identity to key it
 * on, walking from a Guests list searched for "smith" to Tasks leaves "smith" in the box AND calls
 * the TASKS page's `onSearch("smith")` 300ms later, filtering a list the host never searched.
 * ⚠ It CANNOT be derived from the route. A URL-stage drawer (`/guests/:id/edit`) is a different
 * sitemap entry rendering the SAME page instance, and the keyword must survive opening a drawer —
 * which it did when the shell lived inside the page. It also cannot be read from `page` during the
 * shell's render: React runs a child's layout effect before its parent's, so on the render where
 * the route has already changed the store still holds the OUTGOING page's chrome. The identity has
 * to travel WITH the chrome, and it does.
 */
export interface PageChromeSnapshot extends PageChromeValue {
  /** Increments each time a different page instance takes over the header. */
  ownerId: number;
}

/** An empty header — what the shell shows when no page has published anything. */
export const EMPTY_PAGE_CHROME: PageChromeSnapshot = { ownerId: 0 };

interface PageChromeStore {
  /** Replace the published chrome and wake the shell. `owner` identifies the publishing page. */
  publish: (owner: object, chrome: PageChromeValue) => void;
  /**
   * Withdraw a page's chrome as it unmounts — but ONLY while that page is still the publisher.
   *
   * ⚠ THE OWNERSHIP TEST IS NOT OPTIONAL. Nothing we control guarantees that an outgoing page's
   * cleanup runs before its replacement publishes — a page can also be swapped out by something
   * that is not a navigation at all (a plan gate showing the upgrade screen, a guard redirecting),
   * and React's own StrictMode deliberately runs a cleanup/setup cycle on mount. A withdraw that
   * did not check would blank the header of whatever now owns it, and nothing would put it back
   * until that page happened to re-render for an unrelated reason.
   */
  withdraw: (owner: object) => void;
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => PageChromeSnapshot;
}

// ============================================
// Context
// ============================================

const PageChromeContext = createContext<PageChromeStore | null>(null);

// ============================================
// Provider
// ============================================

interface PageChromeProviderProps {
  children: React.ReactNode;
}

export const PageChromeProvider: React.FC<PageChromeProviderProps> = ({ children }) => {
  const currentRef = useRef<PageChromeSnapshot>(EMPTY_PAGE_CHROME);
  const ownerRef = useRef<object | null>(null);
  const ownerIdRef = useRef<number>(0);
  const listenersRef = useRef<Set<() => void>>(new Set());

  const store = useMemo<PageChromeStore>(() => {
    const notify = () => {
      listenersRef.current.forEach((listener) => listener());
    };

    return {
      publish: (owner: object, chrome: PageChromeValue) => {
        if (ownerRef.current !== owner) {
          ownerRef.current = owner;
          ownerIdRef.current += 1;
        }

        currentRef.current = { ...chrome, ownerId: ownerIdRef.current };
        notify();
      },
      withdraw: (owner: object) => {
        if (ownerRef.current !== owner) {
          return;
        }

        ownerRef.current = null;
        currentRef.current = EMPTY_PAGE_CHROME;
        notify();
      },
      subscribe: (listener: () => void) => {
        listenersRef.current.add(listener);

        return () => {
          listenersRef.current.delete(listener);
        };
      },
      getSnapshot: () => currentRef.current,
    };
  }, []);

  return <PageChromeContext.Provider value={store}>{children}</PageChromeContext.Provider>;
};

// ============================================
// Hooks
// ============================================

/**
 * Publish this page's header/footer chrome to the app shell.
 *
 * Called by `PageChrome` — a page renders `<PageChrome …>` rather than calling this directly, so
 * the chrome reads as part of the page's JSX exactly as the old `<DashboardLayout …>` props did.
 *
 * ⓘ A no-op outside a provider (a print page, a public page, a component rendered in isolation),
 * because a page that is not inside the shell has no header to fill.
 */
export const usePublishPageChrome = (chrome: PageChromeValue): void => {
  const store = useContext(PageChromeContext);

  // A stable identity for THIS page instance, so the store can tell "the page that published this
  // is going away" from "a different page has since taken over".
  const ownerRef = useRef<object>({});

  // ⚠ No dependency array: the chrome object is rebuilt on every render of the page and carries
  // that render's handlers. See the file header for why a one-shot publish is a stale-closure bug.
  React.useLayoutEffect(() => {
    store?.publish(ownerRef.current, chrome);
  });

  // ⚠ A SEPARATE, UNMOUNT-ONLY effect. Returning the withdraw from the publish effect above would
  // run it between every pair of renders — clearing the header and re-publishing it on each
  // keystroke, which is two shell renders per page render and a blank frame if either is deferred.
  React.useLayoutEffect(() => {
    const owner = ownerRef.current;

    return () => {
      store?.withdraw(owner);
    };
  }, [store]);
};

/**
 * Read the chrome the current page published. Used by the app shell ONLY.
 *
 * ⚠ `useSyncExternalStore` rather than context state: see the file header — chrome in provider
 * state above the routes re-renders every page and loops.
 */
export const usePageChromeSnapshot = (): PageChromeSnapshot => {
  const store = useContext(PageChromeContext);

  const subscribe = React.useCallback(
    (listener: () => void) => (store ? store.subscribe(listener) : () => {}),
    [store],
  );

  const getSnapshot = React.useCallback(
    () => (store ? store.getSnapshot() : EMPTY_PAGE_CHROME),
    [store],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
